import React, { useEffect, useRef } from 'react';
import { Box, Typography, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { Chart as ChartJS, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { SampleDataBadge } from '../../common';

// Register Chart.js components
ChartJS.register(...registerables, ChartDataLabels);

// Expand icon
const ExpandIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path
            d="M4 16V12M4 16H8M4 16L9 11M16 4V8M16 4H12M16 4L11 9"
            stroke="#666666"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

/**
 * Cumulative Footfall Across All TLs Chart
 * Dual line chart showing MIS Logins and TL Footfall
 */
const MBFCumulativeFootfallChart = ({ data = [], isSampleData = false, onMaximize, isFullscreen = false }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    // Default data
    const defaultData = [
        { week: 'Week 01', mis_logins: 1800, tl_footfall: 1100 },
        { week: 'Week 02', mis_logins: 1500, tl_footfall: 1700 },
        { week: 'Week 03', mis_logins: 2000, tl_footfall: 1700 },
        { week: 'Week 04', mis_logins: 2200, tl_footfall: 2000 },
    ];

    // Transform API data to chart format
    // API returns: [{ week: 'Week 01', mis_logins: 0, tl_footfall: 0 }, ...]
    const chartData = data.length > 0 ? data : defaultData;
    const labels = chartData.map(item => item.week || `Week ${chartData.indexOf(item) + 1}`);
    const misLoginsData = chartData.map(item => item.mis_logins || 0);
    const tlFootfallData = chartData.map(item => item.tl_footfall || 0);

    // Calculate dynamic max for Y-axis
    const allValues = [...misLoginsData, ...tlFootfallData];
    const maxValue = Math.max(...allValues, 0);
    const suggestedMax = maxValue === 0 ? 10 : Math.ceil(maxValue * 1.1);

    useEffect(() => {
        if (chartRef.current) {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            const ctx = chartRef.current.getContext('2d');
            const fontSize = isMobile ? 10 : 12;
            const pointRadius = isMobile ? 4 : 6;

            chartInstance.current = new ChartJS(ctx, {
                type: 'line',
                data: {
                    labels: labels,
                    datasets: [
                        {
                            label: 'MIS Logins',
                            data: misLoginsData,
                            borderColor: '#7987FF',
                            backgroundColor: 'transparent',
                            borderWidth: 2,
                            tension: 0.4,
                            pointRadius: pointRadius,
                            pointBackgroundColor: '#7987FF',
                            pointBorderColor: '#ffffff',
                            pointBorderWidth: 2,
                            pointHoverRadius: pointRadius + 2,
                        },
                        {
                            label: 'TL Footfall',
                            data: tlFootfallData,
                            borderColor: '#FFA5CB',
                            backgroundColor: 'transparent',
                            borderWidth: 2,
                            tension: 0.4,
                            pointRadius: pointRadius,
                            pointBackgroundColor: '#FFA5CB',
                            pointBorderColor: '#ffffff',
                            pointBorderWidth: 2,
                            pointHoverRadius: pointRadius + 2,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                        mode: 'index',
                        intersect: false,
                    },
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            enabled: true,
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            titleColor: '#333333',
                            bodyColor: '#333333',
                            borderColor: '#e6e6e6',
                            borderWidth: 1,
                            padding: 10,
                            cornerRadius: 8,
                        },
                        datalabels: {
                            display: false, // No data labels on chart
                        },
                    },
                    scales: {
                        x: {
                            grid: { display: false },
                            border: { display: false },
                            ticks: {
                                color: '#333333',
                                font: {
                                    family: '"Inter", sans-serif',
                                    size: fontSize,
                                },
                            },
                        },
                        y: {
                            min: 0,
                            suggestedMax: suggestedMax,
                            ticks: {
                                color: '#333333',
                                font: {
                                    family: '"Inter", sans-serif',
                                    size: fontSize,
                                },
                                callback: (value) => value.toLocaleString(),
                            },
                            border: { display: false },
                            grid: { color: '#f0f0f0' },
                        },
                    },
                },
            });
        }

        return () => {
            if (chartInstance.current) chartInstance.current.destroy();
        };
    }, [isMobile, isTablet, labels, misLoginsData, tlFootfallData, suggestedMax]);

    // Fullscreen mode - render just the chart without wrapper
    if (isFullscreen) {
        return (
            <Box sx={{ width: '100%', height: '100%', p: 2 }}>
                <canvas ref={chartRef} />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                bgcolor: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #e6e6e6',
                display: 'flex',
                flexDirection: 'column',
                height: '380px',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    bgcolor: '#ffffff',
                    borderBottom: '1px solid #e6e6e6',
                    padding: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography
                        sx={{
                            color: '#333333',
                            fontFamily: '"Roboto", sans-serif',
                            fontSize: '16px',
                            lineHeight: '20px',
                            fontWeight: 500,
                        }}
                    >
                        Cumulative Footfall Across All TLs
                    </Typography>
                    {isSampleData && <SampleDataBadge absolute={false} />}
                </Box>

                <IconButton
                    size="small"
                    onClick={onMaximize}
                    sx={{
                        bgcolor: '#ffffff',
                        border: '1px solid #e6e6e6',
                        borderRadius: '8px',
                        padding: '8px',
                        boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.05)',
                        '&:hover': { bgcolor: '#f9f9f9' },
                    }}
                >
                    <ExpandIcon />
                </IconButton>
            </Box>

            {/* Content Area */}
            <Box
                sx={{
                    bgcolor: '#fafafa',
                    padding: '12px',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                }}
            >
                {/* Chart Container */}
                <Box
                    sx={{
                        bgcolor: '#ffffff',
                        borderRadius: '8px',
                        border: '1px solid #e6e6e6',
                        padding: '12px',
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                    }}
                >
                    {/* Chart */}
                    <Box sx={{ flex: 1, minHeight: 0 }}>
                        <canvas ref={chartRef} />
                    </Box>

                    {/* Legend */}
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '16px',
                            pt: 2,
                            flexShrink: 0,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Box
                                sx={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    bgcolor: '#7987FF',
                                }}
                            />
                            <Typography
                                sx={{
                                    color: '#333333',
                                    fontFamily: '"Inter", sans-serif',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                }}
                            >
                                MIS Logins
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Box
                                sx={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    bgcolor: '#FFA5CB',
                                }}
                            />
                            <Typography
                                sx={{
                                    color: '#333333',
                                    fontFamily: '"Inter", sans-serif',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                }}
                            >
                                TL Footfall
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default MBFCumulativeFootfallChart;
